import {KonvaEventObject} from "konva/types/Node";
import React, {useState} from "react";
import {distance, setActiveOnTopLayer} from "../../utils";
import {useDispatch, useSelector} from "react-redux";
import {penActions, selectActivePenGroupId, selectPenType} from "./penSlice";
import {ToolHookResult} from "../../models/editor";
import {EditorTool} from "../../models/enums";
import {useSnapToPoint} from "../length/snapUtils";
import {Vector2d} from "konva/types/types";
import {selectAngleSnapThreshold, selectSnappingState, selectStylusOnlyMode, viewActions} from "../view/viewSlice";
import {PEN_TOOL_THRESHOLD} from "../../constants";
import {PenElement} from "./PenElement";
import {useSelectionCleaner} from "../../hooks/select";
import {KonvaEventType, PenGroup, PenLine, PenToolType} from "../../../../../base-konva/types";
import {getId} from "../../../../../../utils";
import {getPointerPosition, getSnappedPosition} from "../../../../../base-konva/utils";
import {ActionCreators} from "redux-undo";

function getPointerEventData(evt: KonvaEventObject<KonvaEventType>): {pointerType: string, pressure: number, buttons: number} {
	const native = evt.evt as MouseEvent | PointerEvent | TouchEvent
	if ('pointerType' in native) {
		return {pointerType: native.pointerType, pressure: native.pressure, buttons: native.buttons}
	}
	if ('touches' in native || 'changedTouches' in native) {
		return {pointerType: 'touch', pressure: 1, buttons: 0}
	}
	return {pointerType: 'mouse', pressure: 1, buttons: native.buttons}
}

function usePen(penDrawGroups: PenGroup[]): ToolHookResult {
	const {snapToPoint} = useSnapToPoint()
	const dispatch = useDispatch();
	const [hookRenderId] = useState(getId());
	const penType = useSelector(selectPenType);
	const snapping = useSelector(selectSnappingState);
	const angleThreshold = useSelector(selectAngleSnapThreshold);
	const activePenGroupId = useSelector(selectActivePenGroupId);
	const stylusOnlyMode = useSelector(selectStylusOnlyMode);
	const [isDrawing, setIsDrawing] = useState(false)
	const [currentLine, setCurrentLine] = useState<PenLine>({id: getId(), name: "-", visible: true, points: []})
	const [currentPressures, setCurrentPressures] = useState<number[]>([])

	useSelectionCleaner(EditorTool.PEN)

	function onMouseDown(evt: KonvaEventObject<KonvaEventType>) {
		const {pointerType, pressure, buttons} = getPointerEventData(evt)

		// Barrel button (buttons & 4) → undo
		if (buttons & 4) {
			dispatch(ActionCreators.undo())
			return
		}

		// Eraser tip (buttons & 32) → skip drawing
		if (buttons & 32) return

		// Stylus-only mode: only accept stylus input
		if (stylusOnlyMode && pointerType !== 'pen') return

		if (penDrawGroups.length === 0 || penDrawGroups.every(group => group.id !== activePenGroupId)) {
			dispatch(penActions.addPenGroup())
		}

		setIsDrawing(true);
		const position = getPointerPosition(evt);
		if (position) {
			setCurrentLine(line => ({
				...line,
				points: line.points.concat([position.x, position.y])
			}))
			setCurrentPressures(p => [...p, pressure])
		}
	}

	function onMouseUp() {
		// Do not update redux state until finished drawing lines,
		// Update on mouse move will cause problem with future undo/redo functionality
		// State should be update after finished whole action
		dispatch(penActions.addPenLine({points: currentLine.points, pressures: currentPressures}))
		setIsDrawing(false)
		setCurrentLine(line => ({...line, points: []}))
		setCurrentPressures([])
	}

	function onMouseMove(evt: KonvaEventObject<KonvaEventType>) {
		const position = getPointerPosition(evt);
		if (position && isDrawing) {
			if (penType === PenToolType.FREE) {
				if (currentLine.points.length >= 2) {
					const lastPosition = currentLine.points.slice(-2)
					if (distance({x: lastPosition[0], y: lastPosition[1]}, position) >= PEN_TOOL_THRESHOLD) {
						const {pressure} = getPointerEventData(evt)
						setCurrentLine(line => ({
							...line,
							points: line.points.concat([position.x, position.y])
						}))
						setCurrentPressures(p => [...p, pressure])
					}
				}
			}
			else {
				const setPoints = (points: number[]) => {
					if (points.length <= 1) return [position.x, position.y]
					const firstPoint: Vector2d = {x: points[0], y: points[1]}
					const newPosition = snapping.point ? snapToPoint(position).snapTo : snapping.angle
						? getSnappedPosition(firstPoint, position, angleThreshold)
						: position
					return [points[0], points[1], newPosition.x, newPosition.y]
				}
				setCurrentLine(line => ({
					...line,
					points: setPoints(line.points)
				}))
			}
		}
	}

	function render() {
		// Current line visualization, without concat only stored lines will be displayed.
		return setActiveOnTopLayer(penDrawGroups, activePenGroupId).map(group => (
			group.penLines
				.concat(group.id === activePenGroupId ? {...currentLine, penType} : [])
				.map(line => (
					<PenElement
						key={line.id}
						line={line}
						group={group}
						isDrawing={isDrawing}/>
				))
		))
	}

	return {
		id: hookRenderId,
		render,
		tool: EditorTool.PEN,
		callbacks: {
			onMouseUp,
			onMouseDown,
			onMouseMove,
		}
	}
}

export default usePen;
